import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { Json } from '@/integrations/supabase/types';

export type ReviewAction = 'approve' | 'reject' | 'correct' | 'escalate' | 'request_evidence';
export type OutputStatus =
  | 'draft'
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'corrected'
  | 'escalated'
  | 'more_evidence_requested';

const STATUS_BY_ACTION: Record<ReviewAction, OutputStatus> = {
  approve: 'approved',
  reject: 'rejected',
  correct: 'corrected',
  escalate: 'escalated',
  request_evidence: 'more_evidence_requested',
};

interface SubmitParams {
  outputId: string;
  currentStatus: OutputStatus;
  currentTenantId: string | null;
  action: ReviewAction;
  notes: string;
  correctedPayload?: Record<string, unknown> | null;
}

export const useAIOutputReview = () => {
  const { user, tenantId } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const submitAction = useCallback(
    async (params: SubmitParams): Promise<{ ok: true } | { ok: false; error: string }> => {
      if (!user) return { ok: false, error: 'Not authenticated' };
      setSubmitting(true);

      const newStatus = STATUS_BY_ACTION[params.action];

      const { error: upErr } = await supabase
        .from('ai_outputs')
        .update({
          status: newStatus,
          reviewer_id: user.id,
          reviewed_at: new Date().toISOString(),
          review_notes: params.notes || null,
          corrected_payload: (params.action === 'correct' ? params.correctedPayload ?? null : null) as Json,
          // Heal pre-existing rows created before tenant_id was populated on
          // insert (same pattern already used in this file before extraction).
          ...(params.currentTenantId === null && tenantId ? { tenant_id: tenantId } : {}),
        })
        .eq('id', params.outputId);

      if (upErr) {
        setSubmitting(false);
        return { ok: false, error: upErr.message };
      }

      const { error: reviewErr } = await supabase.from('ai_output_reviews').insert({
        output_id: params.outputId,
        reviewer_id: user.id,
        action: params.action,
        previous_status: params.currentStatus,
        new_status: newStatus,
        notes: params.notes || null,
        corrected_payload: (params.action === 'correct' ? params.correctedPayload ?? null : null) as Json,
      });

      setSubmitting(false);
      if (reviewErr) return { ok: false, error: reviewErr.message };
      return { ok: true };
    },
    [user, tenantId],
  );

  return { submitting, submitAction };
};
